import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import type { UserLinkRow } from './useUserLinks';
import UserLinksGrid from './UserLinksGrid';

/**
 * The records one person is named on, in a dialog — the Benutzerverwaltung's
 * Verknüpfungen and the Grabungsmannschaft's Erfassungen alike (see
 * UserLinksGrid).
 */
export default function LinkedRecordsDialog({
    open,
    title,
    rows,
    ready = true,
    showProject = true,
    onClose
}: {
    open: boolean;
    title: ReactNode;
    rows: UserLinkRow[];
    /** False while the rows are still being read. */
    ready?: boolean;
    /** Off where every row is the same project. */
    showProject?: boolean;
    onClose: () => void;
}) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            // A fixed height gives the grid a viewport to virtualize into: a
            // worker's Zeiterfassung alone runs to hundreds of rows.
            slotProps={{ paper: { sx: { height: '85vh' } } }}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent
                sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0
                }}
            >
                {!ready ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 4
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : rows.length === 0 ? (
                    <Typography sx={{ color: 'text.secondary', p: 3 }}>
                        <FormattedMessage id="admin.users.links.empty" />
                    </Typography>
                ) : (
                    <Box sx={{ flex: 1, minHeight: 240 }}>
                        <UserLinksGrid rows={rows} showProject={showProject} />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={onClose}
                >
                    <FormattedMessage id="common.close" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
